<script setup lang="ts">
import { computed, ref } from 'vue'
import { useAiChatStore } from '@/stores/ai-chat'
import { Button } from '@kyoto2/components/ui/button'
import { Loader2 } from 'lucide-vue-next'

const props = defineProps<{ routeContext?: string }>()
const store = useAiChatStore()
const pendingAction = ref<'approved' | 'always' | 'rejected' | null>(null)

const rememberable = computed(() => store.pendingApproval?.rememberable === true)
const isPermanent = computed(() => store.pendingApproval?.rememberable === false)

const title = computed(() => {
  const pending = store.pendingApproval
  if (!pending) return ''
  // Prefer the server's plain-language summary; fall back to a generic line.
  return pending.summary?.trim() || 'Allow the assistant to continue'
})

const helper = computed(() => {
  if (isPermanent.value) {
    return 'This permanently deletes data. Allow once, or choose Don’t allow.'
  }
  if (rememberable.value) {
    return 'Allow once, or Always allow for the rest of this chat. Don’t allow tells the assistant to stop.'
  }
  return 'Allow once, or Don’t allow to stop the assistant.'
})

async function approve(remember = false) {
  if (store.isStreaming) return
  pendingAction.value = remember ? 'always' : 'approved'
  try {
    await store.resumeWithApproval('approved', props.routeContext, { remember })
  } finally {
    pendingAction.value = null
  }
}

async function reject() {
  if (store.isStreaming) return
  pendingAction.value = 'rejected'
  try {
    await store.resumeWithApproval('rejected', props.routeContext)
  } finally {
    pendingAction.value = null
  }
}
</script>

<template>
  <div
    v-if="store.pendingApproval"
    class="mx-2.5 mb-2 rounded-2xl border bg-muted/40 p-3.5 space-y-3 min-w-0"
  >
    <div class="space-y-1 min-w-0">
      <p class="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        Needs your OK
      </p>
      <p class="text-sm font-medium leading-snug text-foreground break-words">
        {{ title }}
      </p>
      <p
        v-if="isPermanent"
        class="text-xs text-muted-foreground leading-snug"
      >
        This can’t be undone.
      </p>
    </div>

    <div class="flex flex-wrap items-center gap-2">
      <Button
        size="sm"
        class="h-8"
        :disabled="store.isStreaming || pendingAction !== null"
        @click="approve(false)"
      >
        <Loader2 v-if="pendingAction === 'approved'" class="size-3.5 animate-spin" />
        Allow
      </Button>
      <Button
        v-if="rememberable"
        size="sm"
        variant="outline"
        class="h-8"
        :disabled="store.isStreaming || pendingAction !== null"
        @click="approve(true)"
      >
        <Loader2 v-if="pendingAction === 'always'" class="size-3.5 animate-spin" />
        Always allow
      </Button>
      <Button
        size="sm"
        variant="ghost"
        class="h-8"
        :disabled="store.isStreaming || pendingAction !== null"
        @click="reject"
      >
        <Loader2 v-if="pendingAction === 'rejected'" class="size-3.5 animate-spin" />
        Don’t allow
      </Button>
    </div>

    <p class="text-[11px] text-muted-foreground leading-snug">
      {{ helper }}
    </p>
  </div>
</template>
